import type { AircraftSectionExtractionBinding } from "./aircraftSectionBindingApi";
import type {
  AircraftSectionExtractionCommand,
  AircraftSectionPlane,
  AircraftSectionSliceRequest,
} from "./aircraftSectionExtractionApi";

export type AircraftSectionProjection =
  | "panel-hinge"
  | "xfoil-single-contour"
  | "resolved-multibody";
export type AircraftSectionSolver = "flexfoil" | "2d-rans";

export interface AircraftSectionDraft {
  readonly stationM: number;
  readonly projection: AircraftSectionProjection;
  readonly solver: AircraftSectionSolver;
  readonly clearanceMm: number;
}

export const SECTION_PROJECTIONS: ReadonlyArray<{
  value: AircraftSectionProjection; label: string;
}> = [
  { value: "panel-hinge", label: "Panel hinge" },
  { value: "xfoil-single-contour", label: "XFoil single contour" },
  { value: "resolved-multibody", label: "Resolved multibody" },
];

export const SECTION_SOLVER_CONTRACTS = {
  flexfoil: {
    label: "FlexFoil",
    revision: "sha256:968e11fb9655df50a245a0ebed20a9562e69cb883c9b1002403ea96966bf0c51",
  },
  "2d-rans": {
    label: "2D RANS",
    revision: "sha256:559b3d6e38a13122465ea232026216f424c913f2a4c161282c2ad03d077335ac",
  },
} as const;

export function initialAircraftSectionDraft(
  binding: AircraftSectionExtractionBinding | null,
): AircraftSectionDraft {
  const plane = binding?.defaultPlane;
  const stationM = plane
    ? plane.originM.reduce((sum, value, index) => sum + value * plane.basis[2]![index]!, 0)
    : 0;
  return { clearanceMm: 0.6, projection: "resolved-multibody",
    solver: "flexfoil", stationM };
}

export function validateSectionDraft(value: AircraftSectionDraft): string | null {
  if (!Number.isFinite(value.stationM) || Math.abs(value.stationM) > 1.0e6) {
    return "section-plane-out-of-bounds";
  }
  if (!Number.isFinite(value.clearanceMm) || value.clearanceMm < 0.1
      || value.clearanceMm > 3) return "section-clearance-out-of-bounds";
  return null;
}

function movedPlane(
  source: AircraftSectionPlane,
  stationM: number,
): AircraftSectionPlane {
  const normal = source.basis[2]!;
  const sourceStation = source.originM.reduce(
    (sum, value, index) => sum + value * normal[index]!, 0,
  );
  const delta = stationM - sourceStation;
  return {
    ...source,
    originM: source.originM.map((value, index) => value + delta * normal[index]!),
  };
}

export function buildAircraftSectionSliceRequest(
  binding: AircraftSectionExtractionBinding,
  draft: AircraftSectionDraft,
): AircraftSectionSliceRequest {
  const failure = validateSectionDraft(draft);
  if (failure) throw new Error(failure);
  return {
    schema: "loom.aircraft-section-slice-request/v1",
    aircraftVersionPin: binding.aircraftVersionPin,
    definitionPin: binding.definitionPin,
    occurrence: binding.occurrence,
    poseStatePin: binding.poseStatePin,
    sourceGeometryPin: binding.sourceGeometryPin,
    target: binding.target,
    plane: movedPlane(binding.defaultPlane, draft.stationM),
    extractorRevision: binding.extractorRevision,
    settings: {
      intersectionToleranceM: 0.000001,
      maximumLoops: 128,
      maximumPointsPerLoop: 2048,
    },
  };
}

export function buildAircraftSectionExtractionCommand(
  binding: AircraftSectionExtractionBinding,
  draft: AircraftSectionDraft,
  requestId: string,
): AircraftSectionExtractionCommand {
  return {
    project_id: binding.projectId,
    idempotency_key: `aircraft-section:${requestId}`,
    target: {
      logical_id: binding.sectionArtifactLogicalId,
      name: binding.label,
      metadata: { occurrence_id: binding.occurrence.occurrenceId },
    },
    request_id: requestId,
    slice_request: buildAircraftSectionSliceRequest(binding, draft),
  };
}
